///
/// EarthBrowser
///
/// Created by Matt Giger on 3/24/2024
/// Copyright (c) 2024 Geodata Labs
///

export type easingfunc = (k: number) => number

/**
 * Easing functions for animations.
 */
export class easing {
	/** Linear easing: no acceleration */
	public static linear: easingfunc = k => k

	/** Quadratic easing in: accelerating from zero velocity */
	public static quadraticIn: easingfunc = k => Math.pow(k, 2)

	/** Quadratic easing out: decelerating to zero velocity */
	public static quadraticOut: easingfunc = k => k * (2 - k)

	/** Quadratic easing in and out: acceleration until halfway, then deceleration */
	public static quadraticInOut: easingfunc = k => k < 0.5 ? 2 * Math.pow(k, 2) : -1 + (4 - 2 * k) * k

	/** Cubic easing in: accelerating from zero velocity */
	public static cubicIn: easingfunc = k => Math.pow(k, 3)

	/** Cubic easing out: decelerating to zero velocity */
	public static cubicOut: easingfunc = k => --k * k * k + 1

	/** Cubic easing in and out: acceleration until halfway, then deceleration */
	public static cubicInOut: easingfunc = k => k < 0.5 ? 4 * Math.pow(k, 3) : (k - 1) * (2 * k - 2) * (2 * k - 2) + 1

	/** Exponential easing in: accelerating from zero velocity */
	public static exponentialIn: easingfunc = k => k === 0 ? 0 : Math.pow(1024, k - 1)

	/** Exponential easing out: decelerating to zero velocity */
	public static exponentialOut: easingfunc = k => k === 1 ? 1 : 1 - Math.pow(2, -10 * k)

	/** Exponential easing in and out: acceleration until halfway, then deceleration */
	public static exponentialInOut: easingfunc = k => {
		if(k === 0 || k === 1) {
			return k
		}
		if(k < 0.5) {
			return 0.5 * Math.pow(1024, 2 * k - 1)
		}
		return 0.5 * (-Math.pow(2, -20 * k + 10) + 2)
	}

	/** Elastic easing in: accelerating from zero velocity */
	public static elasticIn: easingfunc = k => {
		if(k === 0 || k === 1) {
			return k
		}
		return -Math.pow(2, 10 * k - 10) * Math.sin((k * 10 - 10.75) * (2 * Math.PI) / 3)
	}

	/** Elastic easing out: decelerating to zero velocity */
	public static elasticOut: easingfunc = k => {
		if(k === 0 || k === 1) {
			return k
		}
		return Math.pow(2, -10 * k) * Math.sin((k * 10 - 0.75) * (2 * Math.PI) / 3) + 1
	}

	/** Elastic easing in and out: acceleration until halfway, then deceleration */
	public static elasticInOut: easingfunc = k => {
		if(k === 0 || k === 1) {
			return k
		}
		if(k < 0.5) {
			return -0.5 * Math.pow(2, 20 * k - 10) * Math.sin((20 * k - 11.125) * (2 * Math.PI) / 4.5)
		}
		return Math.pow(2, -20 * k + 10) * Math.sin((20 * k - 11.125) * (2 * Math.PI) / 4.5) * 0.5 + 1
	}

	/** Bounce easing in: accelerating from zero velocity */
	public static bounceIn: easingfunc = k => 1 - easing.bounceOut(1 - k)

	/** Bounce easing out: decelerating to zero velocity */
	public static bounceOut: easingfunc = k => {
		if(k < 1 / 2.75) {
			return 7.5625 * k * k
		} else if(k < 2 / 2.75) {
			return 7.5625 * (k -= 1.5 / 2.75) * k + 0.75
		} else if(k < 2.5 / 2.75) {
			return 7.5625 * (k -= 2.25 / 2.75) * k + 0.9375
		} else {
			return 7.5625 * (k -= 2.625 / 2.75) * k + 0.984375
		}
	}

	/** Bounce easing in and out: acceleration until halfway, then deceleration */
	public static bounceInOut: easingfunc = k => {
		return k < 0.5 ? easing.bounceIn(k * 2) * 0.5 : easing.bounceOut(k * 2 - 1) * 0.5 + 0.5
	}
}

/**
 * Represents an animation with customizable properties and callbacks.
 */
export class animation {
	/** Starting value of the animation */
	private _start: number = 0

	/** Ending value of the animation */
	private _end: number = 1

	/** Easing function used for the animation */
	private efunc: easingfunc = easing.linear

	/** Callback function called on each update of the animation */
	private updateCallback?: (value: number) => void

	/** Callback function called when the animation completes */
	private completeCallback?: () => void

	/** Duration of the animation in milliseconds */
	private _duration: number = 0

	/** Elapsed time of the animation in milliseconds */
	private _elapsed: number = 0

	/** Flag indicating if the animation is currently running */
	private _running: boolean = false

	/** Timestamp of the last animation frame */
	private lastFrameTime: number = 0

	/** ID of the current animation frame */
	private frame_id: number = 0

	/** Flag indicating if the animation should loop */
	private _loop: boolean = false

	/**
	 * Gets whether the animation is currently running.
	 * @returns {boolean} True if the animation is running, false otherwise.
	 */
	public get running(): boolean {
		return this._running
	}

	/**
	 * Sets the starting value of the animation.
	 * @param {number} start - The starting value.
	 * @returns {animation} The animation instance for chaining.
	 */
	public from(start: number): animation {
		this._start = start
		return this
	}

	/**
	 * Sets the ending value of the animation.
	 * @param {number} end - The ending value.
	 * @returns {animation} The animation instance for chaining.
	 */
	public to(end: number): animation {
		this._end = end
		return this
	}

	/**
	 * Sets the duration of the animation.
	 * @param {number} seconds - The duration in seconds.
	 * @returns {animation} The animation instance for chaining.
	 */
	public duration(seconds: number): animation {
		this._duration = seconds * 1000
		return this
	}

	/**
	 * Sets the easing function for the animation.
	 * @param {easingfunc} easing - The easing function to use.
	 * @returns {animation} The animation instance for chaining.
	 */
	public easing(easing: easingfunc): animation {
		this.efunc = easing
		return this
	}

	/**
	 * Sets the update callback function.
	 * @param {function} callback - The function to call on each update.
	 * @returns {animation} The animation instance for chaining.
	 */
	public onUpdate(callback: (value: number) => void): animation {
		this.updateCallback = callback
		return this
	}

	/**
	 * Sets the complete callback function.
	 * @param {function} callback - The function to call when the animation completes.
	 * @returns {animation} The animation instance for chaining.
	 */
	public onComplete(callback: () => void): animation {
		this.completeCallback = callback
		return this
	}

	/**
	 * Starts the animation.
	 */
	public start(): void {
		if(this._running) {
			cancelAnimationFrame(this.frame_id)
			this.frame_id = 0
			this._running = false
		}

		if(!this._running) {
			this.frame_id = requestAnimationFrame(this.frame.bind(this))
		}
		this._running = true
		this._elapsed = 0
		this.lastFrameTime = 0
	}

	/**
	 * Starts the animation in loop mode.
	 */
	public loop(): void {
		this._loop = true
		this.start()
	}

	/**
	 * Stops the animation.
	 */
	public stop(): void {
		this._running = false
	}

	/**
	 * Handles each animation frame.
	 * @param {DOMHighResTimeStamp} timestamp - The current timestamp.
	 */
	private frame(timestamp: DOMHighResTimeStamp): void {
		if(this._running) {

			if(this.lastFrameTime === 0) {
				this.lastFrameTime = timestamp
			} else {
				this._elapsed += timestamp - this.lastFrameTime
				this.lastFrameTime = timestamp
			}

			if(this.updateCallback) {
				const k = Math.min(this._elapsed / this._duration, 1)
				const value = this._start + (this._end - this._start) * this.efunc(k)
				this.updateCallback(value)
			}

			if(this._elapsed < this._duration) {
				this.frame_id = requestAnimationFrame(this.frame.bind(this))
			} else if(this._loop) {
				this._elapsed = 0
				this.lastFrameTime = 0
				this.frame_id = requestAnimationFrame(this.frame.bind(this))
			} else {
				this._running = false
				this._elapsed = 0
				this.lastFrameTime = 0
				this.completeCallback?.()
				this.frame_id = 0
			}
		}
	}
}
